import LinkWithIcon from './header-links/LinkWithIcon'
import type { HeaderLink } from '../../types/site-header'
import { useEffect, useMemo, useState } from 'react'
import { invalidateMiniCartData, MiniCart } from '@vp/cart-client'
import { getPageInfo } from '../../utils/page-meta'
import useClient from '../../hooks/useClient'
import { Box } from '@vp/swan'
import { useAuth } from '../../hooks/useAuth'
import { useTenantConfig } from '../../context/TenantConfigContext'
import { FALLBACK_ACCOUNT_ID } from '../../constants/config'

declare global {
  interface Window {
    bookends?: {
      useMiniCart?: boolean
    }
  }
  interface Document {
    invalidateMiniCartData?: typeof invalidateMiniCartData
  }
}

const CartSSRComponent = ({ cartLink }: { cartLink: HeaderLink }) => {
  return <LinkWithIcon href={cartLink.url} iconType="cart" label={cartLink.text} />
}

type CartProps = {
  locale: string
  cartLink: HeaderLink
}

const CartCSRComponent = ({ locale, cartLink }: CartProps) => {
  const { auth } = useAuth()
  const tenantConfig = useTenantConfig()
  const [usingMiniCart, setUsingMiniCart] = useState(false)
  const [authInitialized, setAuthInitialized] = useState(false)

  const { pageName, pageSection, pageStage } = getPageInfo()

  useEffect(() => {
    setUsingMiniCart(window?.bookends?.useMiniCart ?? true)
    document.invalidateMiniCartData = invalidateMiniCartData
  }, [])

  useEffect(() => {
    if (auth?.getIdentity()) {
      setAuthInitialized(true)
    }
  }, [auth])

  const { accountId, cartId } = useMemo(() => {
    const site = tenantConfig?.sites?.find((s) => s.locales.primary.split('-')[1]?.toLowerCase() === locale.split('-')[1]?.toLowerCase())
    return {
      accountId: tenantConfig?.accountId || FALLBACK_ACCOUNT_ID,
      cartId: site?.cartId || '',
    }
  }, [tenantConfig, locale])

  if (authInitialized && usingMiniCart && accountId && cartId) {
    return (
      <Box ml={{ sm: 'to-actions' }}>
        <MiniCart
          pageName={pageName!}
          pageSection={pageSection!}
          pageStage={pageStage!}
          auth={auth}
          locale={locale}
          accountId={accountId}
          cartTypeId={cartId}
        />
      </Box>
    )
  }

  return <CartSSRComponent cartLink={cartLink} />
}

export default function Cart({ locale, cartLink }: Readonly<CartProps>) {
  const isClient = useClient()

  if (!isClient) return <CartSSRComponent cartLink={cartLink} />

  return <CartCSRComponent locale={locale} cartLink={cartLink} />
}
